// search.ts — Option 3 (LOCKED 2026-07-12): the frontend half of ON-DEVICE web search.
// Calls the Rust `web_search` command (which browses on the USER'S device+internet via
// DuckDuckGo, keyless), then formats the results so they can be appended to a brain call —
// the model reads them and answers grounded, on ANY BYOK vendor. Oriora holds no search key,
// runs no search server; cost = $0, rate-limits distributed across each user's own device/IP.
// Reusable verbatim across the fleet. See BRAIN-PATTERNS.md → Pattern 7 (Option 3).

// ⚠️ Adapted from the fleet copy: the reference apps import `invoke` from a `./tauri-web` shim that
// falls back to a web runtime. Content has no such shim — it imports the real Tauri `invoke`
// directly and guards on `isTauri` (see App.tsx / iap.tsx). Matched to THIS app's existing pattern
// rather than dragging in a module it doesn't have. On web `isTauri` is false and maybeWebContext
// returns "" — the brain call then proceeds exactly as it did before, ungrounded but never broken.
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "./supabase";

export interface WebResult {
  title: string;
  url: string;
  snippet: string;
}

/** Run an on-device web search. Best-effort: any failure (offline, web target, parse) → []. */
export async function searchWeb(query: string): Promise<WebResult[]> {
  const q = query.trim();
  if (!q) return [];
  try {
    return await invoke<WebResult[]>("web_search", { query: q });
  } catch {
    return [];
  }
}

/** Format results as a labeled block to append to a brain message/question. Empty when no
 *  results, so the brain's Pattern-7 guard makes it fall back to "this is my own reasoning". */
export function formatWebResults(results: WebResult[]): string {
  if (!results.length) return "";
  const lines = results
    .map((r, i) => `${i + 1}. ${r.title} — ${r.snippet} (source: ${r.url})`)
    .join("\n");
  return (
    "\n\n[Live web search results retrieved just now on the user's device — use and CITE the" +
    " relevant ones (name the source URL) in your answer; if none are relevant, say so and" +
    " answer from your own knowledge, flagged as such]:\n" +
    lines
  );
}

/** Search, then READ the top results, and format them for the brain call. "" when nothing came back.
 *
 *  Reading is the point: `formatWebResults` (kept below for other callers) passes only DuckDuckGo's
 *  one-line snippet, so the model had to infer anything the page states plainly. Only 3 pages — a
 *  chat question should not wait on eight fetches. */
export async function webContextFor(query: string, readTop = 3): Promise<string> {
  const results = await searchWeb(query);
  if (!results.length) return "";
  return formatReadResults(await readResults(results, readTop));
}

// A message worth searching for = an information question, not a command/log ("log my walk").
// Keeps the ~1s search off pure-CRUD turns. Errs toward searching when a question mark is present.
const QUESTION_RE =
  /\?|^\s*(who|what|whats|what's|when|where|why|how|which|whose|is|are|was|were|does|do|did|can|could|should|would|will|has|have|tell me|explain|define|describe|list|compare|find|look up|search|latest|current|recent|news|today|price|cost)\b/i;

/** Gate + search: run an on-device web search ONLY when the message reads like an info question,
 *  and return the formatted block to append to a chat `context`. "" otherwise (or on failure). */
export async function maybeWebContext(message: string): Promise<string> {
  // The search + page read are Rust commands, so they only exist on the native surfaces. On the web
  // build `invoke` would reject and every question-shaped turn would pay a rejected promise for
  // nothing — bail before that. (This guard replaces the ./tauri-web shim the fleet copy relies on.)
  if (!isTauri) return "";
  const m = message.trim();
  if (m.length < 4 || !QUESTION_RE.test(m)) return "";
  return webContextFor(m);
}

/** ON-DEVICE page read — the other half of Option 3. `searchWeb` only returns DuckDuckGo's
 *  title+snippet; this opens the actual page on the user's own connection so the model reads
 *  CONTENT rather than inferring from a blurb. Best-effort: a dead link → "" and it is skipped. */
export async function readPage(url: string, maxChars = 6000): Promise<string> {
  try {
    return await invoke<string>("web_fetch", { url, maxChars });
  } catch {
    return "";
  }
}

/** Search, then READ the top `readTop` results. */
export async function readResults(
  results: WebResult[],
  readTop = 3,
): Promise<Array<WebResult & { text: string }>> {
  const head = results.slice(0, readTop);
  const texts = await Promise.all(head.map((r) => readPage(r.url)));
  return head.map((r, i) => ({ ...r, text: texts[i] }));
}

/** Format read pages for the model. Pages that failed to load are LABELLED, never silently
 *  dropped — the model must tell "I could not open this" from "this said nothing", or it
 *  fills the gap itself. */
export function formatReadResults(items: Array<WebResult & { text: string }>): string {
  if (!items.length) return "";
  const blocks = items.map((r, i) =>
    `${i + 1}. ${r.title}\n   URL: ${r.url}\n   ${
      r.text ? `PAGE TEXT: ${r.text}` : `PAGE TEXT: (could not be opened from this device — use only the snippet: ${r.snippet})`
    }`,
  );
  return (
    "\n\n[Pages retrieved AND opened just now on the user's own device. Use and CITE only these;" +
    " dates and specifics are usually stated in the page text, so take them from there rather than" +
    " guessing. If a page could not be opened, say so instead of inferring its contents]:\n" +
    blocks.join("\n\n")
  );
}
